import { DesktopContainer } from '@/components/DesktopContainer/DesktopContainer';
import { H1, H2, H3 } from '@/components/ui/typography';

const Introduction = () => (
    <>
        <H2 variant="type-headline-small">Introduction</H2>
        <div>
            Massachusetts Higher Education Assistance Corporation, d/b/a Britebound™
            (“Britebound” or “we” or “us”) respects your privacy and is committed to protecting it
            through our compliance with this Privacy Policy (“Policy”).
        </div>
        <div>
            This Policy describes the types of information we may directly or indirectly collect from you or receive
            from you when you use our services (the “Services”), including any page, functionality or feature, and all
            content, information, links and other materials (“Materials”) made available on or through the Services. For
            certain Services, you may be able to accumulate points (“Points”) by engaging with Materials and third-party
            services made available on or through our Services (including on websites of our third-party partners) (such
            Materials and third-party services, “Experiences”). For clarity, this Policy does not apply to information
            collected by any third-party website(s) or service(s) that you may access or otherwise intentionally
            interact with on or off of our Services. The handling of the collection, use, and disclosure of information
            about you in such circumstances can be found in the privacy policies governing those third-party websites or
            services.
        </div>
        <div>
            We may aggregate or de-identify information described in this Policy. Aggregate or de-identified
            information that we do not attempt to re-identify is not subject to this Policy.
        </div>
        <div>
            <div>
                As described in greater detail below, this policy applies to information we collect:
            </div>
            <ul className="list-disc pl-6">
                <li>In the course of providing the Services; and</li>
                <li>
                    In any email(s), text(s), and other electronic messages that we send or receive from you in relation
                    to the Services.
                </li>
            </ul>
        </div>
        <div>
            Please read this Policy carefully to understand our practices in relation to your information and how we
            will treat it. If you do not agree with this Policy, you may choose not to use the Services. By accessing or
            using the Services, you acknowledge that you have read, understood, and agree to the terms of this Policy.
        </div>
        <div>
            This Policy may change from time to time (see “Changes to Our Privacy Policy” at the end of this Policy).
            Your continued use of the Services after we make changes represents your acceptance of those changes.
        </div>
    </>
);

const ChildrenUnderAge = () => (
    <>
        <H2 variant="type-headline-small">Children Under the Age of 13</H2>
        <div>
            We designed the Services to avoid collecting personal information from children under 13 years of age, and
            we do not knowingly collect such information. If you are under 13, please do not register, use the Services,
            or provide any personal information.
            In the event we discover that personal information has been collected from a child under 13 without verified
            parental consent, we will delete that information promptly in accordance with applicable law.
        </div>
        <div>
            <div>
                To further support compliance with the Children’s Online Privacy Protection Act (COPPA), we have adopted
                the following practices:
            </div>
            <ul className="list-disc pl-6">
                <li>
                    <strong>Limited Retention:</strong>
                    {' '}
                    Any inadvertently collected personal information from a child under 13 will
                    be retained only as long as necessary to fulfill its purpose and comply with legal obligations.
                </li>
                <li>
                    <div>
                        <strong>Secure Disposal:</strong>
                        {' '}
                        Once no longer needed, such data will be securely deleted or de-identified to prevent
                        unauthorized access or use.
                    </div>
                </li>
                <li>
                    <strong>Parental Requests:</strong>
                    {' '}
                    If a parent or guardian believes their child under 13 has provided personal information and
                    would like it deleted, they may contact us at
                    {' '}
                    <a className="text-blue-600 dark:text-blue-400 underline" href="mailto:support@gozig.com">support@gozig.com</a>
                    , and we will verify and
                    promptly process the request.
                </li>
                <li>
                    <strong>Data Safeguards:</strong>
                    {' '}
                    Any children’s data (if collected inadvertently) is subject to strict security controls to
                    prevent unauthorized access, use, or disclosure.
                </li>
            </ul>
            <div>
                Britebound is committed to protecting children’s privacy and maintaining full compliance with applicable data
                protection laws.
            </div>
        </div>
    </>
);

const InformationWeCollect = () => (
    <>
        <H2 variant="type-headline-small">
            Information We Collect About You and How We Collect It
        </H2>
        <div>
            <div>
                We may collect several types of information from or about you when you use the Services, including:
            </div>
            <ul className="list-disc pl-6">
                <li>
                    Information by which you may be personally identified, such as your name, date of birth, postal
                    address, e-mail address, telephone number, or any other identifier by which you may be identified or
                    contacted online or offline;
                </li>
                <li>
                    Information you voluntarily provide, such as gender or ethnicity;
                </li>
                <li>
                    Information about your activity when using our Services, such as pages visited, links clicked on,
                    Experiences completed, Points accumulated, rewards redeemed, duration of your usage of the Services,
                    and other usage details; and
                </li>
                <li>
                    Information about your internet connection and the device(s) you use to access the Services.
                </li>
            </ul>
        </div>
        <div>
            <div>We collect this information in two ways:</div>
            <ul className="list-disc pl-6">
                <li>
                    Directly from you when you provide it to us; and
                </li>
                <li>
                    Automatically as you use the Services. Information collected automatically may include usage details
                    and IP addresses, as well as information collected through application program interfaces (“APIs”),
                    cookies, web beacons, and other tracking technologies, as further detailed below.
                </li>
            </ul>
        </div>
    </>
);

const InformationYouProvideUs = () => (
    <>
        <H2 variant="type-headline-small">
            Information You Provide to Us
        </H2>
        <div>
            <div>The information we collect from you on or through the Services may include:</div>
            <ul className="list-disc pl-6">
                <li>
                    Information that you provide when using the Services, including information that
                    you provide at the time you register to use an Britebound-operated website. We
                    may also ask you for information when you report a problem with the Services;
                </li>
                <li>
                    Information you provide to us to participate in contests, sweepstakes, and other promotions on or
                    through the Services, such as your name, age, contact details, and parent/guardian contact details;
                </li>
                <li>
                    Records and copies of communications between you and Britebound (including
                    email addresses) if you contact us; and
                </li>
                <li>
                    Your responses to surveys that we might ask you to complete for research purposes.
                </li>
            </ul>
        </div>
        <H3 variant="type-headline-xsmall">Sensitive Information</H3>
        <div>
            We do not knowingly collect, and ask that you do not submit, any sensitive personal information through the
            Services, such as government identification numbers, health or medical information, biometric data,
            financial account information, or information concerning political opinions, religious or philosophical
            beliefs, trade union membership, or sexual orientation.
        </div>
    </>
);

const InformationWeAutomaticallyCollect = () => (
    <>
        <H2 variant="type-headline-small">
            Information We Collect Through Automatic Data Collection Technologies
        </H2>
        <div>
            <div>
                As you navigate through and interact with the Services, we, and our partners and vendors, may use
                automatic data collection technologies to collect and analyze certain information about your equipment,
                browsing actions, Experiences completed, Points accumulated, rewards redeemed, and usage patterns,
                including:
            </div>
            <ul className="list-disc pl-6">
                <li>
                    Details of your visits to a website when using the Services, including traffic data, URL source,
                    generalized location data based on IP address, logs, and other communication data, and the resources
                    that you access and use on the Services; and
                </li>
                <li>
                    Information about your device(s) and internet connection, including your IP address, operating
                    system, and browser type.
                </li>
            </ul>
        </div>
        <div>
            <div>
                The information we collect automatically helps us to deliver and provide better and more personalized
                Services, including by enabling us to:
            </div>
            <ul className="list-disc pl-6">
                <li>
                    Track the completion of Experiences, Points accumulated, and rewards redeemed (as applicable);
                </li>
                <li>
                    Estimate our audience size and usage patterns;
                </li>
                <li>
                    Store information about your preferences, allowing us to customize the Services according to your
                    individual interests;
                </li>
                <li>
                    Speed up your searches; and
                </li>
                <li>
                    Recognize you when you return to the Services.
                </li>
            </ul>
        </div>
        <div>
            <div>
                The technologies we and our partners and vendors use to carry out such automatic data collection may
                include:
            </div>
            <ul className="list-disc pl-6">
                <li>
                    Application Program Interfaces (“APIs”). To track your completion of Experiences on third-party
                    sites, we assign a user ID to you and related information, which is communicated back to us through
                    an API so that we can record completion of such Experiences, allocate Points, and enable you to
                    redeem rewards when eligible. We also use APIs in coordination with our third-party service
                    providers to enable you to participate in contests, sweepstakes, and other promotions on or through
                    the Services.
                </li>
                <li>
                    Cookies (or browser cookies). A cookie is a small file placed on the hard drive of your computer
                    that allows us to understand how users interact with the Services. You may refuse to accept browser
                    cookies by activating the appropriate setting on your browser. However, if you select this setting,
                    you may not be able to access certain parts of the Services. Unless you have adjusted your browser
                    setting so that it will refuse cookies, our system will issue cookies when you direct your browser
                    to the Services
                </li>
                <li>
                    Tracking Pixels. The Services may contain embedded HTML code snippets that
                    enable Britebound to measure the performance of advertising and marketing
                    campaigns carried out on third-party websites and services; and
                </li>
                <li>
                    Web Beacons. The Services may contain small electronic files known as web
                    beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit
                    Britebound, for example, to count users who have visited those pages and
                    analyze other related statistics (for example, recording the popularity of certain
                    content and verifying system and server integrity).
                </li>
            </ul>
        </div>
        <div>
            We retain personal information pursuant to our records retention program for as long as is necessary for the
            purposes set out in this Policy, unless a longer period is required or permitted under applicable law or is
            needed to resolve disputes or protect our legal rights. For example, we may retain certain personal
            information for an additional period of time to prevent, protect against, detect, and respond to incidents
            of fraud on the Services.
        </div>
    </>
);

const HowWeUseYourInformation = () => (
    <>
        <H2 variant="type-headline-small">
            How We Use Your Information
        </H2>
        <div>
            <div>
                We use information that we collect about you or that you provide to us, including any personal
                information, for the following purposes:
            </div>
            <ul className="list-disc pl-6">
                <li>
                    To present and provide you with access to the Services, including interactive features, where
                    applicable;
                </li>
                <li>
                    To recognize you across our Services, including when you use multiple devices or accounts;
                </li>
                <li>
                    To track the completion of Experiences and Points accumulated on the Services;
                </li>
                <li>
                    To enable the redemption of rewards (as applicable);
                </li>
                <li>
                    To enable you to participate in contests, sweepstakes, and other promotions on or through the
                    Services, including by verifying your age to ensure you are eligible to enter for a drawing under
                    the applicable Terms of Use, and to contact you and/or your parent or guardian in the event that
                    your entry is selected;
                </li>
                <li>
                    To evaluate the use, results, and effectiveness of our Services;
                </li>
                <li>
                    To provide you with information or other services that you request from us;
                </li>
                <li>
                    To fulfill any other purpose for which you provide it;
                </li>
                <li>
                    To carry out our obligations and enforce our rights arising out of this Policy, the Terms of Use,
                    which are accessible here (
                    <a href="/terms-of-use" className="text-blue-600 dark:text-blue-400 underline">https://gozig.com/terms-of-use</a>
                    ), and any other legal terms applicable to use of
                    the Services;
                </li>
                <li>
                    To notify you about changes to the Services;
                </li>
                <li>
                    To protect the rights, property, or safety of Britebound, our customers, or others;
                </li>
                <li>
                    To prevent, protect against, detect, and respond to incidents of fraud on the Services;
                </li>
                <li>
                    To measure the performance of our advertising and marketing campaigns, including through third-party
                    websites and services;
                </li>
                <li>
                    In any other way we may describe when you provide the information; and
                </li>
                <li>
                    For any other purpose with your consent.
                </li>
            </ul>
        </div>
        <H3 variant="type-headline-xsmall">Opting Out of Communications</H3>
        <div>
            We may send you occasional promotional emails or text messages related to the Services. You can opt out of
            receiving these communications at any time by following the instructions included in the message (e.g.,
            clicking “unsubscribe” in emails or replying “STOP” to text messages). Please note that even if you opt out
            of promotional communications, we may still send you administrative or service-related messages related to
            your account.
        </div>
    </>
);

const Disclosure = () => (
    <>
        <H2 variant="type-headline-small">
            Disclosure of Your Information
        </H2>
        <div>
            We may disclose information without restriction that does not identify an individual, including information
            in aggregated or de-identified form. This may include aggregated information gathered via cookies, web
            beacons, and tracking pixels.
        </div>
        <div>
            <div>
                We may disclose personal information that we collect for the following purposes:
            </div>
            <ul className="list-disc pl-6">
                <li>
                    To vendors, contractors, and other third parties that support us in providing the Services and who
                    are bound by contract to keep personal information confidential and use it only for the business
                    purposes for which we disclose it to them;
                </li>
                <li>
                    To third parties that provide a feature, functionality or service that you have otherwise requested;
                </li>
                <li>
                    To fulfill the purpose(s) for which you provided it;
                </li>
                <li>
                    For any other purpose disclosed by us when you provide the information;
                </li>
                <li>
                    To comply with any court order, law, or legal process, including to respond to any government or
                    regulatory request;
                </li>
                <li>
                    To enforce or apply this Policy, the Terms of Use, or any other terms that apply to the use of the
                    Services;
                </li>
                <li>
                    If we believe disclosure is necessary or appropriate to protect the rights, property,
                    or safety of Britebound, our customers, or others;
                </li>
                <li>
                    As part of a change in business structure or ownership of Britebound, we may
                    disclose personal information to the buyer or other successor in the event of a
                    merger, divestiture, restructuring, reorganization, dissolution, or other sale or
                    transfer of some or all of Britebound’s assets, whether as a going concern or as
                    part of bankruptcy, liquidation, or similar proceeding, in which personal
                    information held by Britebound about users of the Services is among the assets
                    transferred; and
                </li>
                <li>
                    For any other purpose with your consent.
                </li>
            </ul>
        </div>
        <div>
            We do not sell your personal information, nor does Britebound display any targeted
            advertising on Britebound-operated websites.
        </div>
        <div>
            Britebound does share data with advertising service providers to measure the
            performance of our advertising and marketing campaigns on other websites, apps, or
            services, including on Facebook, Snapchat, various Google services, and other social
            networks.
        </div>
        <div>
            Per the above, please note that we use our best efforts and take multiple steps to avoid the collection of
            information for targeted advertising purposes from children under 13 years of age, including by prohibiting
            children under 13 years of age from creating a user profile on the Services. In addition, we prohibit access
            to certain areas of the Services by children under 13 years of age in this Policy.
        </div>
    </>
);

const PersonalInformationRights = () => (
    <>
        <H2 variant="type-headline-small">
            Personal Information Rights
        </H2>
        <div>
            Applicable law may recognize certain rights in relation to personal information, including the right to
            access, correct, or delete personal information, restrict the processing of personal information (including
            sensitive personal information, as applicable), and data portability, among others.
        </div>
        <div>
            You can review and update your personal information by logging into the Services and visiting your account
            profile page. You may also send us an email at
            {' '}
            <a className="text-blue-600 dark:text-blue-400 underline" href="mailto:support@gozig.com">support@gozig.com</a>
            {' '}
            to exercise rights applicable to your
            personal information, or to have an authorized agent do so on your behalf. We will respond to such requests
            in accordance with applicable law, and we may request further information from you to verify your identity
            and/or verify the identity of your authorized agent (as well as confirm their authority to act on your
            behalf). We may not accommodate a request if we believe the change would violate any law or legal
            requirement, cause the information to be incorrect, or otherwise is subject to an applicable exception under
            law. We will inform you in accordance with applicable law if we do not accommodate your request. You may
            have the right to appeal our determination under local law, which can also be exercised by contacting us at
            {' '}
            <a className="text-blue-600 dark:text-blue-400 underline" href="mailto:support@gozig.com">support@gozig.com</a>
            . We will not discriminate against you when you exercise personal information rights.
        </div>
    </>
);

const DataTransfers = () => (
    <>
        <H2 variant="type-headline-small">
            Data Transfers
        </H2>
        <div>
            As set out in our
            {' '}
            <a href="/terms-of-use" className="text-blue-600 dark:text-blue-400 underline">Terms of Use</a>
            , our Services are strictly for use by and solely directed to persons located
            in the United States. If you are not in the United States, YOU MAY NOT ACCESS OR USE THE SERVICES. If you
            access or use the Services from outside the United States, you do so at your own risk and are responsible
            for compliance with local laws.
        </div>
    </>
);

const DataSecurity = () => (
    <>
        <H2 variant="type-headline-small">
            Data Security
        </H2>
        <div>
            We have implemented industry-standard security technologies, procedures, and organizational measures
            designed to secure your personal information from accidental loss and or unauthorized access, use,
            alteration, or disclosure.
        </div>
        <div>
            The safety and security of your information also depends on you. If you choose or are provided with a
            username, a password, or any other information as part of our security procedures, you must keep that
            information confidential and not disclose it to any other person or entity. You may not provide any other
            person with access to the Services or portions of it using your user name, password, or other security
            information. You are responsible for all activity occurring under your account or with use of your username
            and password.
        </div>
        <div>
            You agree to notify us immediately of any unauthorized access to or use of your username or password, or any
            other breach of security of which you become aware. You may report unauthorized access by sending an email
            to
            {' '}
            <a className="text-blue-600 dark:text-blue-400 underline" href="mailto:support@gozig.com">support@gozig.com</a>
            .
        </div>
        <div>
            Unfortunately, the transmission of information via the internet is not completely secure. Although we do our
            best to protect your personal information, we cannot guarantee the security of your personal information
            transmitted to the Services. Any transmission of personal information is at your own risk. We are not
            responsible for circumvention of any privacy settings or security measures contained on the Services.
        </div>
    </>
);

const ChangeOurPrivacyPolicy = () => (
    <>
        <H2 variant="type-headline-small">
            Changes to Our Privacy Policy
        </H2>
        <div>
            We may revise and/or update this Policy from time to time in our sole discretion. The date this Policy was
            last revised is identified at the top of the page. If we make material changes to how we treat our users’
            personal information, we will notify users with an account by email to the email address specified in your
            account. You are responsible for ensuring we have an up-to-date active and deliverable email address where
            we can reach you if you have an account, and for periodically visiting the Services and this Policy to check
            for any changes, regardless of whether you have an account or not.
        </div>
    </>
);

const ContactInformation = () => (
    <>
        <H2 variant="type-headline-small">
            Contact Information
        </H2>
        <div>
            To ask questions or comment about this Policy and our privacy practices, or to register a complaint or
            concern, contact us at
            {' '}
            <a className="text-blue-600 dark:text-blue-400 underline" href="mailto:support@gozig.com">support@gozig.com</a>
            .
        </div>
    </>
);

interface PrivacyPolicyProps {
    showTitle: boolean;
    showLastModified: boolean;
}

export const PrivacyPolicy = ({ showTitle, showLastModified }: PrivacyPolicyProps) => (
    <DesktopContainer>
        <div className="flex flex-col gap-10 px-6">
            {(showTitle || showLastModified) && (
                <div className="flex flex-col gap-4">
                    {showTitle && (
                        <H1 variant="type-headline-large">Privacy Policy</H1>
                    )}
                    {showLastModified && (
                        <div>
                            Last Modified: March 23, 2026
                        </div>
                    )}
                </div>
            )}
            <div className="flex flex-col gap-4 text-base">
                <Introduction />
                <ChildrenUnderAge />
                <InformationWeCollect />
                <InformationYouProvideUs />
                <InformationWeAutomaticallyCollect />
                <HowWeUseYourInformation />
                <Disclosure />
                <PersonalInformationRights />
                <DataTransfers />
                <DataSecurity />
                <ChangeOurPrivacyPolicy />
                <ContactInformation />
            </div>
        </div>
    </DesktopContainer>
);
